import React, { useState } from 'react'
import { useTranslation } from 'react-i18next'
import {
  Modal,
  Toggle,
  ContextNotification,
  Highlight,
} from '@pandiafi/components'
import { Button, Dropdown, Label } from '@pandiafi/design-system'
import { DateTime } from 'luxon'

import type { SharedArea } from '../../api/apiHooks/useAPIGetSharedAreas'
import { useAPIGetContractSuspense } from '../../api/apiHooks/useAPIGetContract'
import { useCurrentContract } from '../../hooks/useCurrentContract/useCurrentContract'
import {
  useAPICreateRightOfAccess,
  RightOfAccessStarts,
} from '../../api/apiHooks/useAPICreateRightOfAccess'
import { useModuleConfig } from '../../hooks/useModuleConfig/useModuleConfig'
import { getPriceForDate } from './utils'

import { ReservationRulesAndTermsLinks } from 'components'

interface GymReservationModalProps {
  gymArea: SharedArea | null
  onClose: () => void
}

export const GymReservationModal: React.FC<GymReservationModalProps> = ({
  gymArea,
  onClose,
}) => {
  const { t } = useTranslation(['common', 'gymBooking'])
  const { currentContractId } = useCurrentContract()
  const { data: contract } = useAPIGetContractSuspense(currentContractId)
  const [rulesAccepted, setRulesAccepted] = useState(false)
  const [showSuccess, setShowSuccess] = useState(false)
  const [errorMsg, setErrorMsg] = useState<string | null>(null)

  const today = DateTime.now()
  const nextMonthStart = today.plus({ months: 1 }).startOf('month')
  const isFirstDayOfMonth = today.day === 1

  const options = [
    { id: 'today', label: t('gymBooking:reservation.today') },
    {
      id: 'next_month',
      label: `${nextMonthStart.toFormat('d.M.yyyy')}`,
    },
  ]

  const [selectedStartTime, setSelectedStartTime] = useState<
    'today' | 'next_month'
  >('today')

  const selectedDate =
    selectedStartTime === 'today'
      ? (today.toISODate() ?? '')
      : (nextMonthStart.toISODate() ?? '')

  const moduleConfig = useModuleConfig()

  const createRightOfAccessMutation =
    useAPICreateRightOfAccess(currentContractId)

  if (!gymArea) return null

  const handleClose = () => {
    onClose()
  }

  const contractStartDate = contract.startDate
  const todayDate = today.toISODate()
  const isBeforeContractStart =
    contractStartDate && todayDate ? todayDate < contractStartDate : false

  const priceAtSelectedDate = getPriceForDate(gymArea, selectedDate)

  const startTimeText = DateTime.fromISO(selectedDate).toFormat('d.M.yyyy')

  const moduleLinks = moduleConfig?.gym
  const gymFallbackLinks: { href: string; label: string }[] = []
  if (moduleLinks?.contractDocumentLink) {
    gymFallbackLinks.push({
      href: moduleLinks.contractDocumentLink,
      label: t('gymBooking:modal.gymRulesLink'),
    })
  }
  if (moduleLinks?.termsOfUseDocumentLink) {
    gymFallbackLinks.push({
      href: moduleLinks.termsOfUseDocumentLink,
      label: t('gymBooking:modal.gymAccessTermsLink'),
    })
  }

  const handleConfirm = () => {
    if (!gymArea) return

    if (isBeforeContractStart) {
      setErrorMsg(t('gymBooking:reservationModal.contractNotStartedError'))
      return
    }

    setErrorMsg(null)

    const selectedDateTime = DateTime.fromISO(selectedDate)

    const starts: RightOfAccessStarts =
      selectedDateTime.month === today.month &&
      selectedDateTime.year === today.year
        ? RightOfAccessStarts.TODAY
        : RightOfAccessStarts.NEXT_MONTH

    createRightOfAccessMutation.mutate(
      {
        sharedAreaId: gymArea.id,
        starts,
      },
      {
        onSuccess: () => {
          setShowSuccess(true)
        },
      }
    )
  }

  return (
    <Modal
      show
      onClose={handleClose}
      title={t('gymBooking:modal.title')}
      key={`gym-modal-${showSuccess}`}
      headerElement={
        showSuccess ? (
          <Highlight>{t('gymBooking:reservationModal.successTitle')}</Highlight>
        ) : (
          <Highlight>{t('gymBooking:modal.headerReserve')}</Highlight>
        )
      }
    >
      {showSuccess ? (
        <>
          <Modal.Notifications>
            <ContextNotification type="success">
              <div>
                <div className="text-lg mb-4">
                  {t('gymBooking:reservationModal.successMessage')}
                </div>
                <div className="text-sm text-gray-600">
                  {t('gymBooking:reservationModal.accessStartsLabel')}{' '}
                  <strong>{startTimeText}</strong>
                </div>
                {priceAtSelectedDate && (
                  <div className="text-sm text-gray-600 mt-2">
                    {t('gymBooking:reservationModal.priceLabel')}{' '}
                    <strong>
                      {priceAtSelectedDate.priceIncludingVat}{' '}
                      {t('gymBooking:reservationModal.perMonth')}
                    </strong>
                  </div>
                )}
              </div>
            </ContextNotification>
          </Modal.Notifications>
          <Modal.Buttons>
            <Button variant="cancel" onClick={handleClose}>
              {t('common:closeButton')}
            </Button>
          </Modal.Buttons>
        </>
      ) : (
        <>
          <Modal.Content>
            {errorMsg && (
              <ContextNotification type="error" className="mb-4">
                {errorMsg}
              </ContextNotification>
            )}
            <div className="text-lg mb-4">
              <span className="font-bold">
                {t('gymBooking:reservation.title')},{' '}
                {gymArea.buildingPartAddress.streetName}{' '}
                {gymArea.buildingPartAddress.streetNumber}
              </span>
            </div>
            <div className="pb-4 w-1/2">
              <Label className="mb-2">
                {t('gymBooking:reservationModal.reservationStartsLabel')} *
              </Label>
              <Dropdown
                options={options}
                value={options.find((opt) => opt.id === selectedStartTime)}
                onChange={(option) => {
                  if (option) {
                    setSelectedStartTime(option.id as 'today' | 'next_month')
                  }
                }}
                disabled={isFirstDayOfMonth}
              />
            </div>
            {priceAtSelectedDate && (
              <div>
                <p>
                  <span className="font-semibold">
                    {t('gymBooking:modal.priceLabel')}{' '}
                    {priceAtSelectedDate.priceIncludingVat}{' '}
                    {t('gymBooking:modal.perMonth')}
                  </span>{' '}
                  {t('gymBooking:timeFormatting.priceAddedToContract')}{' '}
                  {t('gymBooking:timeFormatting.startingFrom')}{' '}
                  <span className="font-semibold">{startTimeText}</span>
                </p>
                <p>
                  {gymArea.buildingPartAddress.streetName}{' '}
                  {gymArea.buildingPartAddress.streetNumber}{' '}
                  {gymArea.buildingPartLocationDetails}
                </p>
              </div>
            )}
            <div className="pt-6">
              <ReservationRulesAndTermsLinks
                contractId={currentContractId}
                reservableCategory="gym"
                realPropertyHierarchyId={gymArea.realPropertyHierarchyId}
                fallbackLinks={gymFallbackLinks}
              />
            </div>
            <ContextNotification type="info" className="mt-10">
              <Toggle
                checked={rulesAccepted}
                onChange={setRulesAccepted}
                labelPosition="right"
              >
                {t('gymBooking:reservationModal.acceptRulesPrefix')}{' '}
                {gymArea.buildingPartAddress.streetName}{' '}
                {gymArea.buildingPartAddress.streetNumber}{' '}
                {t('gymBooking:reservationModal.acceptRulesSuffix')}
              </Toggle>
            </ContextNotification>
          </Modal.Content>

          <Modal.Buttons>
            <Button variant="cancel" onClick={handleClose}>
              {t('gymBooking:modal.cancel')}
            </Button>
            <Button
              variant="primary"
              onClick={handleConfirm}
              disabled={!rulesAccepted || createRightOfAccessMutation.isPending}
              loading={createRightOfAccessMutation.isPending}
            >
              {t('gymBooking:modal.confirm')}
            </Button>
          </Modal.Buttons>
        </>
      )}
    </Modal>
  )
}
